import IconCheck from 'assets/icons/check-2.svg';
import IconError from 'assets/icons/error.svg';
import IconInfo from 'assets/icons/info.svg';
import IconWarning from 'assets/icons/warning.svg';
import cn from 'classnames';
import React from 'react';
import { FormattedMessage } from 'react-intl';

interface PropTypes
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  buttonText?: string;
  className?: string;
  icon?: React.ReactNode;
  message: string;
  messageValues?: Record<
    string,
    | string
    | number
    | boolean
    | Date
    | null
    | undefined
    | ((chunks: React.ReactNode) => React.ReactNode)
  >;
  title?: string;
  type: 'error1' | 'error2' | 'general' | 'info' | 'success' | 'warning';
  onClick?: () => void;
}

/**
 * Alert component that displays messages with various states and optional action button
 */
const Alert: React.FC<PropTypes> = ({
  buttonText,
  className,
  icon,
  message,
  messageValues,
  title,
  type,
  onClick,
}) => {
  const classes = {
    info: 'bg-info-background text-info-content',
    warning: 'bg-warning-background text-warning-content',
    error1: 'bg-error-background text-error-content',
    error2: 'bg-error-main text-error-contrast',
    success: 'bg-success-background text-success-content',
    general: 'bg-background-default text-text-light-bg-secondary',
  };

  const defaultIcon = {
    info: <IconInfo className="h-[22px] w-[22px] text-info-main" />,
    warning: <IconWarning className="h-[22px] w-[22px] text-warning-main" />,
    error1: <IconError className="h-[22px] w-[22px] text-error-main" />,
    error2: <IconError className="h-[22px] w-[22px] text-error-contrast" />,
    success: <IconCheck className="h-[22px] w-[22px] text-success-main" />,
    general: null,
  };

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-lg p-2',
        classes[type],
        className
      )}
      data-test={`alert-${type}`}
    >
      {icon ?? defaultIcon[type]}
      <div className="flex-1">
        {title && (
          <span className="block font-medium" data-test={`alert-title-${type}`}>
            <FormattedMessage id={title} />
          </span>
        )}
        <span
          className="block w-full text-sm"
          data-test={`alert-message-${type}`}
        >
          <FormattedMessage id={message} values={messageValues} />
        </span>
      </div>
      {buttonText && (
        <button
          className="ml-auto whitespace-nowrap px-1.5 py-1 text-sm font-medium"
          onClick={onClick}
          data-test={`alert-button-${type}`}
        >
          <FormattedMessage id={buttonText} />
        </button>
      )}
    </div>
  );
};

export default Alert;
